import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import LotteryCodeField from '../../components/LotteryCodeField';
import DateField from '../../components/DateField';
import { fetchTicketsAnnules } from '../../api/ticketsAnnules';
import { todayISO } from '../../utils/date';
import { bankLabel } from '../../utils/bank';
import './TicketsAnnulesPage.css';

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Ticket.deleted_by (2026-09-17) : 'bank' (POS, le vendeur annule sa propre
// fiche) ou 'company'/'superadmin (impersonation)' (portail web) ; None pour
// les fiches annulees avant l'ajout de ce champ.
const deletedByLabel = (v) => {
  if (!v) return '—';
  if (v === 'bank') return 'Banque';
  if (v === 'company') return 'Admin';
  if (v.startsWith('superadmin')) return 'Superadmin';
  return v;
};

const TicketsAnnulesPage = () => {
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [lotteryFrom, setLotteryFrom] = useState('');
  const [lotteryFromName, setLotteryFromName] = useState('');
  const [lotteryUp, setLotteryUp] = useState('');
  const [lotteryUpName, setLotteryUpName] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const filterApiValue = () => {
    if (filter === 'Groupe') return 'Group';
    if (filter === 'Superviseur') return 'Supervisor';
    if (filter === 'Banque') return 'Bank';
    return 'All';
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await fetchTicketsAnnules({
        dateFrom, dateTo, filter: filterApiValue(), scope: filter === 'Tout' ? undefined : scopeId,
        tirageFrom: lotteryFrom, tirageUp: lotteryUp,
      });
      setData(result);
    } catch (err) {
      setError('Recherche impossible.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <p className="ta-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/rapports" className="bc-link">Rapports</Link>
        {' >> '}
        <span className="bc-current">Tickets Annulaient</span>
      </p>

      <div className="ta-card">
        <div className="ta-card-header">
          <span className="ta-card-title">Rapport Fiches Annules</span>
        </div>
        <div className="ta-card-body">
          <div className="ta-radio-row">
            {['Tout', 'Groupe', 'Superviseur', 'Banque'].map((opt) => (
              <label key={opt} className="ta-radio-label">
                <input
                  type="radio"
                  name="filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="ta-row ta-scope-row">
              <label className="ta-label">{filter}</label>
              {filter === 'Banque' ? (
                <BankScopeField
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, name) => { setScopeId(id); setScopeName(name); }}
                  inputClass="ta-input ta-input-sm"
                  iconClass="ta-icon-btn"
                  nameClass="ta-lottery-name"
                  errClass="ta-lookup-err"
                />
              ) : (
                <ScopeField
                  filter={filter}
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                  inputClass="ta-input ta-input-sm"
                  iconClass="ta-icon-btn"
                  nameClass="ta-lottery-name"
                  errClass="ta-lookup-err"
                />
              )}
            </div>
          )}

          <form onSubmit={handleSearch} className="ta-form">
            <div className="ta-row">
              <label className="ta-label">De</label>
              <DateField className="ta-input ta-input-date" value={dateFrom} onChange={setDateFrom} />

              <label className="ta-label ta-label-to">A</label>
              <DateField className="ta-input ta-input-date" value={dateTo} onChange={setDateTo} />
            </div>

            <div className="ta-row">
              <label className="ta-label">Loterie De</label>
              <LotteryCodeField
                value={lotteryFrom}
                name={lotteryFromName}
                onChange={(code) => { setLotteryFrom(code); setLotteryFromName(''); }}
                onResolve={(code, nm) => { setLotteryFrom(code); setLotteryFromName(nm); }}
                inputClass="ta-input ta-input-sm"
                iconClass="ta-icon-btn"
                nameClass="ta-lottery-name"
              />

              <label className="ta-label ta-label-to">Loterie Up</label>
              <LotteryCodeField
                value={lotteryUp}
                name={lotteryUpName}
                onChange={(code) => { setLotteryUp(code); setLotteryUpName(''); }}
                onResolve={(code, nm) => { setLotteryUp(code); setLotteryUpName(nm); }}
                inputClass="ta-input ta-input-sm"
                iconClass="ta-icon-btn"
                nameClass="ta-lottery-name"
              />
            </div>

            {error && <p className="ta-error">{error}</p>}
            <button type="submit" className="ta-search-btn" disabled={loading}>
              {loading ? 'Recherche...' : 'Chercher'}
            </button>
          </form>
        </div>
      </div>

      {data && (
        <div className="ta-card ta-results">
          <div className="ta-card-header"><span className="ta-card-title">Reporte de Tickets Anulados</span></div>
          <div className="ta-card-body">
            <p className="ta-info-bar">Desde: {data.date_from} Hasta: {data.date_to}</p>

            {data.groups.length === 0 ? (
              <p className="ta-empty">Aucune fiche annulée sur cette portée/période.</p>
            ) : (
              <>
                {data.groups.map((g) => (
                  <div key={g.bank_id} className="ta-group">
                    <p className="ta-group-title">Banca: {bankLabel(g.bank_seq, g.bank_name)}</p>
                    <table className="ta-table">
                      <thead>
                        <tr>
                          <th>Fecha Venta</th><th>Loteria</th><th>O/L</th><th>No. Ticket</th>
                          <th>Hora</th><th>Fecha Anulado</th><th>Hora Anulado</th><th>Monto</th>
                          <th>Anulado Por</th><th>Usuario</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.rows.map((r) => (
                          <tr key={r.ticket_id}>
                            <td>{r.sale_date}</td>
                            <td>{r.tirage_code}-{r.tirage_name}</td>
                            <td>{r.online ? 'O' : 'L'}</td>
                            <td>{r.no ?? '—'}</td>
                            <td>{r.sale_time}</td>
                            <td>{r.deleted_date ?? '—'}</td>
                            <td>{r.deleted_time ?? '—'}</td>
                            <td>{money(r.total_amount)}</td>
                            <td>{deletedByLabel(r.deleted_by)}</td>
                            <td>{r.bank_username}</td>
                          </tr>
                        ))}
                        <tr className="ta-total-row">
                          <td>Total:</td>
                          <td colSpan={2}>Cant. Tickets</td>
                          <td><strong>{g.totals.tickets}</strong></td>
                          <td colSpan={2}></td>
                          <td>Monto Total</td>
                          <td colSpan={3}><strong>{money(g.totals.montant)}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}

                <div className="ta-grand-total">
                  <span>Gran Tickets: {data.grand_totals.tickets}</span>
                  <span>Gran Total: {money(data.grand_totals.montant)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default TicketsAnnulesPage;
